import { CONTENT_TYPES } from "@/constants"

const BLOCK_ELEMENT_PRIORITIES = {
	[CONTENT_TYPES.IMAGE]: 1,
	[CONTENT_TYPES.MEDIUM_IMAGE]: 2,
	[CONTENT_TYPES.LARGE_IMAGE]: 5,
	[CONTENT_TYPES.GALLERY]: 4,
	[CONTENT_TYPES.HIGHLIGHTED_HEADING_AND_DESCRIPTION]: 3,
	[CONTENT_TYPES.DOWNLOAD_FILES]: 0,
}

const BLOCK_ELEMENT_TYPES = {
	BLOCK: "block",
	HEADING: "heading",
	BLOCKQUOTE: "blockquote",
}

const isValidData = (data) => {
	return data && data.value && data.value.document && data.value.document.children && data.blocks
}

const createBlocksMap = (blocks) => {
	return blocks.reduce((acc, block) => {
		acc[block.id] = block
		return acc
	}, {})
}

const isBlockType = (element, blocksMap, blockTypePriorities) => {
	if (!element || !element.type || element.type !== BLOCK_ELEMENT_TYPES.BLOCK) return false

	const block = element.item ? blocksMap[element.item] : null
	return block && blockTypePriorities[block.__typename] !== undefined
}

const isHeadingType = (element) => {
	return element && element.type === BLOCK_ELEMENT_TYPES.HEADING
}

const isBlockquoteType = (element) => {
	return element && element.type === BLOCK_ELEMENT_TYPES.BLOCKQUOTE
}

const getBlockPriority = (blockElement, blocksMap, blockTypePriorities) => {
	if (!blockElement || !blockElement.type || blockElement.type !== BLOCK_ELEMENT_TYPES.BLOCK) return 0

	const block = blockElement.item ? blocksMap[blockElement.item] : null
	return block && blockTypePriorities[block.__typename] ? blockTypePriorities[block.__typename] : 0
}

const getAdjacentElementsInfo = (children, index, blocksMap, blockTypePriorities) => {
	const prevElement = index > 0 ? children[index - 1] : null
	const nextElement = index < children.length - 1 ? children[index + 1] : null

	const isPrevBlock = isBlockType(prevElement, blocksMap, blockTypePriorities)
	const isNextBlock = isBlockType(nextElement, blocksMap, blockTypePriorities)

	const isPrevHeading = isHeadingType(prevElement)
	const isNextHeading = isHeadingType(nextElement)

	const isPrevBlockquote = isBlockquoteType(prevElement)
	const isNextBlockquote = isBlockquoteType(nextElement)

	return {
		prevElement,
		nextElement,
		isPrevBlock,
		isNextBlock,
		isPrevHeading,
		isNextHeading,
		isPrevBlockquote,
		isNextBlockquote,
	}
}

const calculateBlockMargins = (element, adjacentInfo, currentPriority, blocksMap, blockTypePriorities) => {
	const { isPrevBlock, isNextBlock, prevElement, nextElement } = adjacentInfo

	const prevPriority = isPrevBlock ? getBlockPriority(prevElement, blocksMap, blockTypePriorities) : 0
	const nextPriority = isNextBlock ? getBlockPriority(nextElement, blocksMap, blockTypePriorities) : 0

	if (!isPrevBlock && !isNextBlock) {
		// Between two non-block elements
		return { marginTop: true, marginBottom: true }
	} else if (isPrevBlock && isNextBlock) {
		// Between two block elements - use priority to determine margins
		return {
			marginTop: prevPriority < currentPriority,
			marginBottom: nextPriority <= currentPriority,
		}
	} else if (!isPrevBlock && isNextBlock) {
		// Between non-block and block
		return {
			marginTop: true,
			marginBottom: currentPriority >= nextPriority,
		}
	} else {
		// Between block and non-block
		return {
			marginTop: currentPriority > prevPriority,
			marginBottom: true,
		}
	}
}

const calculateHeadingMargins = (adjacentInfo) => {
	const { isPrevBlock, isNextBlock } = adjacentInfo

	if (!isPrevBlock && !isNextBlock) {
		return { marginTop: true, marginBottom: true }
	} else if (isPrevBlock && isNextBlock) {
		return { marginTop: false, marginBottom: false }
	} else if (!isPrevBlock && isNextBlock) {
		return { marginTop: true, marginBottom: false }
	} else {
		return { marginTop: false, marginBottom: true }
	}
}

const calculateBlockquoteMargins = (adjacentInfo) => {
	const { isPrevBlock, isNextBlock, isPrevHeading, isNextHeading } = adjacentInfo

	const hasPrevHigherPriorityElement = isPrevBlock || isPrevHeading
	const hasNextHigherPriorityElement = isNextBlock || isNextHeading

	if (!hasPrevHigherPriorityElement && !hasNextHigherPriorityElement) {
		// Between two non-priority elements
		return { marginTop: true, marginBottom: true }
	} else if (hasPrevHigherPriorityElement && hasNextHigherPriorityElement) {
		// Between two priority elements - blockquote has lowest priority
		return { marginTop: false, marginBottom: false }
	} else if (!hasPrevHigherPriorityElement && hasNextHigherPriorityElement) {
		// Between non-priority and priority element
		return { marginTop: true, marginBottom: false }
	} else {
		// Between priority and non-priority element
		return { marginTop: false, marginBottom: true }
	}
}

export function processStructuredTextMargins(data, blockTypePriorities = BLOCK_ELEMENT_PRIORITIES) {
	if (!isValidData(data)) {
		return data
	}

	const newData = JSON.parse(JSON.stringify(data))
	const { children } = newData.value.document
	const blocksMap = createBlocksMap(newData.blocks)

	children.forEach((child, index) => {
		const adjacentInfo = getAdjacentElementsInfo(children, index, blocksMap, blockTypePriorities)

		if (child.type === BLOCK_ELEMENT_TYPES.BLOCK && child.item) {
			const block = blocksMap[child.item]

			if (block && blockTypePriorities[block.__typename] !== undefined) {
				const currentPriority = blockTypePriorities[block.__typename]
				const margins = calculateBlockMargins(child, adjacentInfo, currentPriority, blocksMap, blockTypePriorities)

				block.marginTop = margins.marginTop
				block.marginBottom = margins.marginBottom
			}
		} else if (isHeadingType(child)) {
			const margins = calculateHeadingMargins(adjacentInfo)

			child.marginTop = margins.marginTop
			child.marginBottom = margins.marginBottom
		} else if (isBlockquoteType(child)) {
			const margins = calculateBlockquoteMargins(adjacentInfo)

			child.marginTop = margins.marginTop
			child.marginBottom = margins.marginBottom
		}
	})

	return newData
}
